import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    IconButton,
    Tooltip,
    Badge
} from '@mui/material';
import {
    AdminPanelSettings,
    Search,
    Edit,
    Refresh,
    FilterList,
    Assignment,
    Person,
    Schedule,
    CheckCircle,
    Error,
    Warning,
    Logout
} from '@mui/icons-material';
import api from '../axios/axiosInstance';

const AdminTicketManagement = ({ onLogout }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [filters, setFilters] = useState({
        status: '',
        priority: '',
        issue_type: '',
        search: ''
    });
    const [summary, setSummary] = useState({});
    const [editDialog, setEditDialog] = useState({
        open: false,
        ticket: null,
        status: '',
        priority: ''
    });

    const statusOptions = [
        { value: 'open', label: 'Open', color: '#ed6c02', icon: <Schedule /> },
        { value: 'in_progress', label: 'In Progress', color: '#1976d2', icon: <Assignment /> },
        { value: 'resolved', label: 'Resolved', color: '#2e7d32', icon: <CheckCircle /> }
    ];

    const priorityOptions = [
        { value: 'low', label: 'Low', color: '#4caf50' },
        { value: 'medium', label: 'Medium', color: '#ff9800' },
        { value: 'high', label: 'High', color: '#f44336' },
        { value: 'urgent', label: 'Urgent', color: '#9c27b0' }
    ];

    const issueTypes = [
        'Billing/Subscription',
        'Students',
        'Assessment',
        'Teacher Resources',
        'Subject',
        'Resources',
        'Other'
    ];

    useEffect(() => {
        fetchTickets();
    }, [filters]);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            
            if (filters.status) params.append('status', filters.status);
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.issue_type) params.append('issue_type', filters.issue_type);
            if (filters.search) params.append('search', filters.search);

            const response = await api.get(`/auth/admin/tickets/?${params.toString()}`);
            setTickets(response.data.tickets);
            setSummary(response.data.summary);
        } catch (error) {
            if (error.response?.status === 403) {
                showAlert('error', 'Access denied. You are not authorized to view this page.');
            } else {
                showAlert('error', 'Failed to load tickets. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            priority: '',
            issue_type: '',
            search: ''
        });
    };

    const openEditDialog = (ticket) => {
        setEditDialog({
            open: true,
            ticket,
            status: ticket.status,
            priority: ticket.priority
        });
    };

    const closeEditDialog = () => {
        setEditDialog({
            open: false,
            ticket: null,
            status: '',
            priority: ''
        });
    };

    const handleUpdateTicket = async () => {
        if (!editDialog.ticket) return;

        setUpdating(editDialog.ticket.id);
        
        try {
            const updateData = {};
            if (editDialog.status !== editDialog.ticket.status) {
                updateData.status = editDialog.status;
            }
            if (editDialog.priority !== editDialog.ticket.priority) {
                updateData.priority = editDialog.priority;
            }

            if (Object.keys(updateData).length === 0) {
                showAlert('info', 'No changes made to the ticket.');
                closeEditDialog();
                return;
            }

            const response = await api.patch(`/auth/admin/tickets/${editDialog.ticket.id}/`, updateData);
            
            // Update the ticket in the list
            setTickets(prev => prev.map(ticket => 
                ticket.id === editDialog.ticket.id 
                    ? response.data.ticket 
                    : ticket
            ));

            showAlert('success', response.data.message);
            closeEditDialog();
            
        } catch (error) {
            showAlert('error', error.response?.data?.error || 'Failed to update ticket.');
        } finally {
            setUpdating(null);
        }
    };

    const getStatusInfo = (status) => {
        return statusOptions.find(s => s.value === status) || statusOptions[0];
    };

    const getPriorityInfo = (priority) => {
        return priorityOptions.find(p => p.value === priority) || priorityOptions[1];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AdminPanelSettings sx={{ fontSize: 40, color: '#d32f2f', mr: 2 }} />
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                            Admin Ticket Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage and track all user support tickets
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => fetchTickets()}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<Logout />}
                        onClick={onLogout}
                        sx={{
                            '&:hover': {
                                backgroundColor: '#c62828',
                            }
                        }}
                    >
                        Logout
                    </Button>
                </Box>
            </Box>

            {/* Alert */}
            {alert.show && (
                <Alert severity={alert.type} sx={{ mb: 3 }}>
                    {alert.message}
                </Alert>
            )}

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#f5f5f5' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                {summary.total || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Tickets
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#fff3e0' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                                {summary.open || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Open
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                {summary.in_progress || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                In Progress
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e8f5e8' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                {summary.resolved || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Resolved
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterList sx={{ mr: 1 }} />
                        <Typography variant="h6">Filters</Typography>
                        <Button size="small" onClick={clearFilters} sx={{ ml: 'auto' }}>
                            Clear All
                        </Button>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search tickets..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                InputProps={{
                                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    label="Status"
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    {statusOptions.map((status) => (
                                        <MenuItem key={status.value} value={status.value}>
                                            {status.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    value={filters.priority}
                                    label="Priority"
                                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                                >
                                    <MenuItem value="">All Priorities</MenuItem>
                                    {priorityOptions.map((priority) => (
                                        <MenuItem key={priority.value} value={priority.value}>
                                            {priority.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Issue Type</InputLabel>
                                <Select
                                    value={filters.issue_type}
                                    label="Issue Type"
                                    onChange={(e) => handleFilterChange('issue_type', e.target.value)}
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    {issueTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Tickets Table */}
            <Card>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Ticket #</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Issue Type</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tickets.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                        <Assignment sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                        <Typography color="text.secondary">
                                            No tickets found matching your filters
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tickets.map((ticket) => {
                                    const statusInfo = getStatusInfo(ticket.status);
                                    const priorityInfo = getPriorityInfo(ticket.priority);
                                    
                                    return (
                                        <TableRow key={ticket.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    #{ticket.ticket_number}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {ticket.user_info ? (
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {ticket.user_info.first_name} {ticket.user_info.last_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {ticket.user_info.email}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Deleted User
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {ticket.title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    icon={statusInfo.icon}
                                                    label={statusInfo.label}
                                                    sx={{ 
                                                        bgcolor: statusInfo.color,
                                                        color: 'white',
                                                        '& .MuiChip-icon': { color: 'white' }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={priorityInfo.label}
                                                    sx={{ 
                                                        bgcolor: priorityInfo.color,
                                                        color: 'white'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {ticket.issue_type}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(ticket.created_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit ticket">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openEditDialog(ticket)}
                                                        disabled={updating === ticket.id}
                                                    >
                                                        {updating === ticket.id ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <Edit />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Edit Dialog */}
            <Dialog open={editDialog.open} onClose={closeEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edit Ticket #{editDialog.ticket?.ticket_number}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <strong>Title:</strong> {editDialog.ticket?.title}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={editDialog.status}
                                        label="Status"
                                        onChange={(e) => setEditDialog(prev => ({
                                            ...prev,
                                            status: e.target.value
                                        }))}
                                    >
                                        {statusOptions.map((status) => (
                                            <MenuItem key={status.value} value={status.value}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {status.icon}
                                                    <Typography sx={{ ml: 1 }}>
                                                        {status.label}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        value={editDialog.priority}
                                        label="Priority"
                                        onChange={(e) => setEditDialog(prev => ({
                                            ...prev,
                                            priority: e.target.value
                                        }))}
                                    >
                                        {priorityOptions.map((priority) => (
                                            <MenuItem key={priority.value} value={priority.value}>
                                                {priority.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditDialog}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleUpdateTicket}
                        disabled={updating === editDialog.ticket?.id}
                    >
                        {updating === editDialog.ticket?.id ? 'Updating...' : 'Update Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminTicketManagement;